import { Company } from '../Model/Company';
import { Entrepreneur } from '../Model/Entrepreneur';
import { Aggreg8Account } from '../Model/Aggreg8Account';
import { BankConfigQueryArgs } from '../Model/BankConfig';
import { storeApi } from './storeApi';

export function isEntrepreneur(data: Entrepreneur | Company[]): data is Entrepreneur {
  return (data as Entrepreneur).companies !== undefined;
}

export const companyApi = storeApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanyList: builder.query<Company[], void>({
      query: () => '/companies',
      transformResponse: (response: Company[]) => {
        if (isEntrepreneur(response)) {
          return response.companies;
        }
        return response;
      },
      providesTags: (result) => {
        return result
          ? [...result.map(({ _id }) => ({ type: 'Company', _id } as const)), { type: 'Company', id: 'LIST' }]
          : [{ type: 'Company', id: 'LIST' }];
      },
    }),
    getCompany: builder.mutation<Company, Partial<Company>>({
      query: ({ _id }) => ({
        url: `/company/${_id}`,
      }),
    }),
    createCompany: builder.mutation<Company, Partial<Company>>({
      query: ({ taxNumber, name }) => ({
        url: `/company`,
        method: 'POST',
        body: { tax_number: taxNumber, company_name: name },
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    updateCompany: builder.mutation<Company, Partial<Company>>({
      query: ({ _id, taxNumber, name }) => ({
        url: `/company/${_id}`,
        method: 'PUT',
        body: { tax_number: taxNumber, company_name: name },
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    patchCompanyAggreg8accounts: builder.mutation<
      Company,
      Pick<Company, '_id'> & { aggreg8Account: Pick<Aggreg8Account, '_id' | 'accountNumber'> }
    >({
      query: ({ _id, aggreg8Account }) => ({
        url: `/company/aggreg8accounts/${_id}`,
        method: 'PATCH',
        body: { account_id: aggreg8Account._id, account_number: aggreg8Account.accountNumber },
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    patchCompanyBankConfigs: builder.mutation<void, BankConfigQueryArgs>({
      query: ({ companyId, bankConfig }) => ({
        url: `/company/bankConfig/${companyId}`,
        method: 'PATCH',
        body: bankConfig,
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    generateMail: builder.mutation<
        Company,
        Pick<Company, '_id'>
    >({
      query({ _id }) {
        return {
          url: `/company/courierEmail/${_id}/`,
          method: 'POST',
        };
      },
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
  }),
});

export const {
  useLazyGetCompanyListQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  usePatchCompanyAggreg8accountsMutation,
  usePatchCompanyBankConfigsMutation,
  useGetCompanyMutation,
  useGenerateMailMutation,
} = companyApi;
